/**
 * Modal xem nhanh thông tin BĐS trong admin.
 *
 * Luồng dữ liệu:
 * - Nếu có propertyId: fetch chi tiết qua propertyApi.getAdminProperty.
 * - Nếu chỉ có property object: dùng trực tiếp (preview từ liên hệ).
 * - Gallery có lightbox portal; đóng lightbox trước khi đóng modal.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { propertyApi } from '../../features/properties/api/propertyApi';
import { getTransactionLabel, PROPERTY_FEATURE_OPTIONS, PROPERTY_ROOM_SPEC_OPTIONS, PUBLICATION_STATUS_OPTIONS } from '../../shared/constants/property';
import { UI_MESSAGES } from '../../shared/constants/messages';
import { TEXT } from '../../shared/languages';
import { formatCurrency } from '../../shared/utils/formatCurrency';
import { ImageLightbox } from '../Common/ImageLightbox';
import { AdminModal } from './AdminModal';

const formatViewCount = (viewCount) => new Intl.NumberFormat('en-US').format(viewCount || 0);

const getOptionLabel = (options, value, fallback = '') =>
  options.find((option) => option.value === value)?.label || fallback || value || '';

/** Ghép thông số phòng (phòng ngủ, WC...) thành chuỗi */
const formatRoomSpecs = (property) =>
  PROPERTY_ROOM_SPEC_OPTIONS.map(({ fieldName, label }) => {
    const count = Number(property[fieldName]);

    if (!Number.isFinite(count) || count <= 0) {
      return null;
    }

    return `${count} ${label}`;
  })
    .filter(Boolean)
    .join(' · ');

const getImageUrl = (image) => image?.url || image?.image_url;

/**
 * Modal xem thông tin chi tiết BĐS (admin quick view).
 * @param {Object} props
 * @param {Object} [props.property] - Dữ liệu BĐS có sẵn (optional)
 * @param {string|number|null} [props.propertyId] - ID để fetch nếu cần
 * @param {boolean} [props.isOpen] - Trạng thái mở modal
 * @param {Function} props.onClose - Callback đóng modal
 */
export const AdminPropertyInfoModal = ({
  property,
  propertyId = null,
  isOpen = Boolean(propertyId || property),
  onClose,
}) => {
  const [loadedProperty, setLoadedProperty] = useState(property || null);
  const [isLoading, setIsLoading] = useState(Boolean(propertyId));
  const [errorMessage, setErrorMessage] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const t = TEXT.property.infoModal;
  const common = TEXT.common;

  useEffect(() => {
    if (!isOpen) {
      setLightboxIndex(null);
    }
  }, [isOpen]);

  // Fetch chi tiết BĐS khi mở modal với propertyId
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    if (!propertyId) {
      setLoadedProperty(property || null);
      setIsLoading(false);
      setErrorMessage('');
      return undefined;
    }

    let isMounted = true;

    setIsLoading(true);
    setErrorMessage('');
    setLoadedProperty(property || null);

    propertyApi
      .getAdminProperty(propertyId)
      .then((response) => {
        if (isMounted) {
          setLoadedProperty(response.data.data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setErrorMessage(UI_MESSAGES.propertyLoadError);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, property, propertyId]);

  const galleryImages = [...(loadedProperty?.images || [])].sort(
    (firstImage, secondImage) => (firstImage.order ?? 0) - (secondImage.order ?? 0)
  );
  const galleryUrls = useMemo(
    () =>
      galleryImages
        .map((image) => getImageUrl(image))
        .filter(Boolean),
    [galleryImages]
  );
  const featureLabels = PROPERTY_FEATURE_OPTIONS.filter(
    (featureOption) => loadedProperty?.features?.[featureOption.resourceKey]
  );

  /** Đóng lightbox trước; nếu không có lightbox thì đóng modal */
  const handleModalClose = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex(null);
      return;
    }

    onClose?.();
  };

  return (
    <>
    <AdminModal
      isOpen={isOpen}
      onClose={handleModalClose}
      panelClassName={`admin-info-modal ${!loadedProperty && isLoading ? 'admin-info-modal-loading' : ''}`.trim()}
      ariaLabelledby="property-info-title"
    >
      {!loadedProperty && isLoading ? (
        <>
          <div className="spinner" />
          <p>{UI_MESSAGES.loadingProperty}</p>
        </>
      ) : loadedProperty ? (
        <>
          <div className="admin-info-modal-header">
            <div>
              <span className="eyebrow">{t.eyebrow}</span>
              <h2 id="property-info-title">{loadedProperty.title}</h2>
            </div>
            <button className="button ghost" type="button" onClick={handleModalClose}>
              {common.close}
            </button>
          </div>

          {errorMessage && <div className="error-box">{errorMessage}</div>}

          <div className="admin-info-section admin-info-section--gallery">
            <h3>{t.images}</h3>
            {isLoading ? (
              <div className="admin-info-gallery-loading">
                <div className="spinner" />
                <p>{UI_MESSAGES.loadingProperty}</p>
              </div>
            ) : galleryImages.length ? (
              <div className="admin-info-gallery">
                {galleryImages.map((image, index) => {
                  const imageUrl = getImageUrl(image);

                  if (!imageUrl) {
                    return null;
                  }

                  return (
                    <figure
                      key={image.id || `${imageUrl}-${index}`}
                      className={`admin-info-gallery-item ${image.is_cover ? 'is-cover' : ''}`}
                    >
                      <button
                        type="button"
                        className="admin-info-gallery-trigger"
                        onClick={() => setLightboxIndex(index)}
                        aria-label={t.openImage.replace('{index}', String(index + 1))}
                      >
                        <img src={imageUrl} alt={`${loadedProperty.title} ${index + 1}`} loading="lazy" />
                      </button>
                      {image.is_cover && (
                        <span className="admin-info-gallery-badge">{t.coverBadge}</span>
                      )}
                    </figure>
                  );
                })}
              </div>
            ) : (
              <p className="hint-text">{t.noImages}</p>
            )}
          </div>

          <div className="admin-info-grid">
            <div>
              <span>{t.price}</span>
              <strong>{formatCurrency(loadedProperty.price)}</strong>
            </div>
            <div>
              <span>{t.transaction}</span>
              <strong>{getTransactionLabel(loadedProperty.transaction_type)}</strong>
            </div>
            <div>
              <span>{t.category}</span>
              <strong>{loadedProperty.category?.name || common.uncategorized}</strong>
            </div>
            <div>
              <span>{t.publicationStatus}</span>
              <strong>
                {getOptionLabel(
                  PUBLICATION_STATUS_OPTIONS,
                  loadedProperty.publication_status,
                  common.notUpdated
                )}
              </strong>
            </div>
            <div>
              <span>{t.address}</span>
              <strong>{loadedProperty.address || loadedProperty.location}</strong>
            </div>
            <div>
              <span>{t.region}</span>
              <strong>
                {[loadedProperty.ward, loadedProperty.district, loadedProperty.province]
                  .filter(Boolean)
                  .join(', ') || loadedProperty.location}
              </strong>
            </div>
            <div>
              <span>{t.area}</span>
              <strong>
                {loadedProperty.area
                  ? `${loadedProperty.area} ${common.areaUnit}`
                  : common.notUpdated}
              </strong>
            </div>
            <div>
              <span>{t.bedroomBathroom}</span>
              <strong>{formatRoomSpecs(loadedProperty) || common.notUpdated}</strong>
            </div>
            <div>
              <span>{t.views}</span>
              <strong>{formatViewCount(loadedProperty.views)}</strong>
            </div>
          </div>

          <div className="admin-info-section">
            <h3>{common.description}</h3>
            <p>{loadedProperty.description || t.noDescription}</p>
          </div>

          {!!featureLabels.length && (
            <div className="admin-info-section">
              <h3>{common.amenities}</h3>
              <div className="admin-info-tags">
                {featureLabels.map(({ label }) => (
                  <span key={label}>{label}</span>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}
    </AdminModal>

    {lightboxIndex !== null &&
      createPortal(
        <ImageLightbox
          images={galleryUrls}
          initialIndex={lightboxIndex}
          alt={loadedProperty?.title || ''}
          className="image-lightbox--above-admin"
          onClose={() => setLightboxIndex(null)}
        />,
        document.body
      )}
    </>
  );
};
